import React, { useEffect, useRef } from 'react';
import {
  Slider, Form, FormInstance, Segmented, InputNumber,
} from 'antd';

import styles from '../../index.module.scss';
import { TipType, TipTypeOptions } from '../../../../../store/statistic';
import DisplayCountryById from '../../../../Common/DisplayCountryById';

export interface TipFormValues {
  tipType: TipType;
  tipValue: number | null;
}

interface StepProps {
  form: FormInstance<TipFormValues>;
  title?: React.ReactNode;
  countryId?: number;
}

export const valueMapDollar = [0, 1, 2, 3, 4, 5, 10, 15, 20];
export const valueMapPercent = [0, 1, 2, 3, 4, 5, 10, 15, 20, 30];

export default function LastStep({
  form,
  title,
  countryId,
}: StepProps): React.ReactElement {
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const isDragging = useRef(false);

  const tipType = Form.useWatch('tipType', form) ?? '%';
  const tipValue = Form.useWatch('tipValue', form) ?? 5;

  const maxValue = tipType === '%'
    ? valueMapPercent[valueMapPercent.length - 1]
    : valueMapDollar.length - 1;

  useEffect(() => {
    form.setFieldsValue({ tipType: '%', tipValue: 5 });
  }, []);

  const marksPercent = {
    0: { label: <span className={styles.markLabel}>No Tip</span> },
    5: '5%',
    10: '10%',
    15: '15%',
    18: '18%',
    20: '20%',
    22: '22%',
    25: '25%',
    30: { label: <span className={styles.markLabel}>30+%</span> },
  };

  const marksDollar = valueMapDollar.reduce((acc, val, idx) => {
    acc[idx] = idx === 0
      ? { label: <span className={styles.markLabel}>No Tip</span> }
      : `$${val}${idx === valueMapDollar.length - 1 ? '+' : ''}`;

    return acc;
  }, {} as Record<number, string | { label: React.ReactNode }>);

  const valueMap = tipType === '%' ? valueMapPercent : valueMapDollar;

  const handleMove = (clientX: number) => {
    if (!sliderRef.current) return;

    const { left, width } = sliderRef.current.getBoundingClientRect();
    let relativeValue = ((clientX - left) / width) * maxValue;

    // Clamp to valid range
    relativeValue = Math.max(0, Math.min(maxValue, relativeValue));

    if (tipType === '%') {
      const step = 5;

      relativeValue = Math.round(relativeValue / step) * step;
    } else {
      let index = Math.round(
        (relativeValue / maxValue) * (valueMap.length - 1),
      );

      index = Math.max(0, Math.min(valueMap.length - 1, index));
      relativeValue = valueMap[index];
    }

    form.setFieldsValue({ tipValue: relativeValue });
  };

  const handleMouseDown = () => {
    isDragging.current = true;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging.current) handleMove(e.clientX);
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  const handleTouchStart = () => {
    isDragging.current = true;
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (isDragging.current) handleMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', handleTouchEnd);
  };
  const getTipPosition = () => {
    if (tipType === '%') {
      return `${(tipValue / maxValue) * 100}%`;
    }

    /* eslint-disable no-nested-ternary, unicorn/no-nested-ternary */
    const index = valueMap.includes(tipValue)
      ? valueMap.indexOf(tipValue)
      : tipValue < 0
        ? 0
        : tipValue > valueMap[maxValue]
          ? maxValue
          : valueMap.findIndex((value) => value >= tipValue) ?? maxValue;

    return `${(index / (valueMap.length - 1)) * 100}%`;
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={(values) => {
        console.log('LastStep', values);
      }}
    >
      {title ?? (
        <>
          <h4 className={styles.title} style={{ marginTop: '30px' }}>
            {countryId === 233 ? (
              <>
                How much do you expect to be tipped <br /> for average service at your job?
              </>
            ) : (
              <>
                How much do you tip for average service at a restaurant in
                {' '}<DisplayCountryById id={countryId} />?
              </>
            )}

          </h4>

          <div style={{ textAlign: 'center', marginTop: '10px' }}>
            <span>
              Average service refers to service that meets basic expectations{' '}
              <br />
              without standing out as exceptional or poor.
            </span>
          </div>
        </>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px 30px',
          margin: '0 0 40px 0',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span>Tip by:</span>
          <Form.Item
            name="tipType"
            initialValue="%"
            noStyle
            layout="horizontal"
          >
            <Segmented
              options={TipTypeOptions}
              value={tipType}
              onChange={(value) => form.setFieldsValue({ tipValue: 5, tipType: value as TipType })}
              className={styles.segmented}
              size="large"
              style={{ margin: '0' }}
            />
          </Form.Item>
        </div>

        {tipType === '$' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>Custom Amount</span>
            <Form.Item name="tipValue" noStyle>
              <InputNumber
                style={{ width: '107px' }}
                size="large"
                placeholder="$0"
                onChange={(value) => form.setFieldsValue({ tipValue: value })}
                min={0}
              />
            </Form.Item>
          </div>
        )}
      </div>

      <Form.Item name="tipValue" initialValue={5}>
        <div className={`${styles.wrapper}`}>
          <div className={styles.sliderWrapper} ref={sliderRef}>
            <Slider
              min={0}
              max={tipType === '%' ? maxValue : valueMap.length - 1}
              step={tipType === '%' ? null : 1}
              marks={tipType === '%' ? marksPercent : marksDollar}
              value={
                tipType === '%'
                  ? tipValue
                  : (() => {
                    // Find the closest index manually
                    const index = valueMap.indexOf(tipValue);

                    if (index !== -1) return index;

                    if (tipValue < valueMap[0]) return 0;
                    if (tipValue > valueMap[valueMap.length - 1]) return valueMap.length - 1;

                    // Find the first index where valueMap[i] >= tipValue
                    const closestIndex = valueMap.findIndex(
                      (v) => v >= tipValue,
                    );

                    return closestIndex !== -1
                      ? closestIndex
                      : valueMap.length - 1;
                  })()
              }
              onChange={(value) => {
                const mappedValue = tipType === '%' ? value : valueMap[value];

                form.setFieldsValue({ tipValue: mappedValue });
              }}
              tooltip={{ formatter: null }}
              className={styles.slider}
            />
            <div
              tabIndex={-1}
              role="button"
              className={styles.selectedTip}
              style={{ left: getTipPosition() }}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
            >
              <div className={styles.item}>
                {`${tipType === '$' ? '$' : ''}${tipValue}${
                  tipType === '%' ? '%' : ''
                }`}
              </div>
            </div>
          </div>
        </div>
      </Form.Item>
    </Form>
  );
}
